import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import styled, { keyframes, css } from 'styled-components';
import citiesData from '../../data/cities_with_coordinates.json';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissionCheck } from '../../hooks/usePermissionCheck';
import { API_BASE_URL } from '../../lib/apiConfig';
import { apiFetch } from '../../lib/apiFetch';

// ─── Types ────────────────────────────────────────────────────────────────────

type DemandType = 'INDIVIDUAL' | 'CIDADE' | 'ENTIDADE' | 'GRUPO';
type DemandSize  = 'PEQUENA' | 'MEDIA' | 'GRANDE';
type DemandNature = 'EXCLUSIVA' | 'INTERMEDIADA';

interface Eleitor { id: number; nome: string; telefone?: string; }

export interface DemandFormData {
  titulo: string;
  descricao: string;
  tipo: DemandType | '';
  tamanho: DemandSize | '';
  natureza: DemandNature | '';
  cidade_id: number | null;
  solicitante_id: number | null;
  intermediador: string;
  previsao_conclusao: string;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: DemandFormData) => Promise<void>;
  initialCityId?: number | null;
  zIndexOverride?: number;
}

// ─── Static data ──────────────────────────────────────────────────────────────

const demandTypes: { value: DemandType; label: string }[] = [
  { value: 'INDIVIDUAL', label: 'Individual' },
  { value: 'CIDADE',     label: 'Cidade' },
  { value: 'ENTIDADE',   label: 'Entidade' },
  { value: 'GRUPO',      label: 'Grupo' },
];

const demandSizes: { value: DemandSize; label: string }[] = [
  { value: 'PEQUENA', label: 'Pequena' },
  { value: 'MEDIA',   label: 'Média' },
  { value: 'GRANDE',  label: 'Grande' },
];

const demandNatures: { value: DemandNature; label: string }[] = [
  { value: 'EXCLUSIVA',   label: 'Exclusiva' },
  { value: 'INTERMEDIADA', label: 'Intermediada' },
];

const allCities = (citiesData as { id: number; nome: string }[])
  .map(c => ({ id: c.id, nome: c.nome }))
  .sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR'));

// ─── Optional fields ──────────────────────────────────────────────────────────

type OptKey = 'descricao' | 'tamanho' | 'natureza' | 'solicitante' | 'prazo';

const OPTIONAL_FIELDS: { key: OptKey; label: string; icon: React.ReactNode }[] = [
  { key: 'descricao',  label: 'Descrição',            icon: <IconText /> },
  { key: 'tamanho',    label: 'Tamanho',              icon: <IconRuler /> },
  { key: 'natureza',   label: 'Natureza',             icon: <IconScale /> },
  { key: 'solicitante',label: 'Solicitante',          icon: <IconUser /> },
  { key: 'prazo',      label: 'Previsão de conclusão',icon: <IconCalendar /> },
];

// ─── Animations ───────────────────────────────────────────────────────────────

const popIn = keyframes`
  from { transform: translateY(10px) scale(.985); opacity: 0; }
  to   { transform: none; opacity: 1; }
`;

const fieldSlideIn = keyframes`
  from { opacity: 0; transform: translateY(-6px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const tickPop = keyframes`
  from { transform: scale(.4); opacity: 0; }
  to   { transform: scale(1); opacity: 1; }
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
`;

// ─── Styled components ────────────────────────────────────────────────────────

const Overlay = styled.div<{ $open: boolean; $z?: number }>`
  position: fixed; inset: 0; z-index: ${p => p.$z ?? 3000};
  background: radial-gradient(120% 80% at 50% 20%, rgba(11,18,32,.18), rgba(11,18,32,.44));
  display: flex; align-items: center; justify-content: center;
  padding: 24px;
  opacity: ${p => p.$open ? 1 : 0};
  pointer-events: ${p => p.$open ? 'all' : 'none'};
  transition: opacity .2s;
  @media (max-width: 560px) { padding: 0; align-items: flex-end; }
`;

const ModalShell = styled.div`
  width: 520px; max-width: 100%;
  max-height: min(88vh, 800px);
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 1px 1px rgba(16,24,40,.04), 0 12px 32px -8px rgba(16,24,40,.18), 0 24px 60px -12px rgba(16,24,40,.22);
  display: grid;
  grid-template-rows: auto 1fr auto;
  overflow: hidden;
  border: 1px solid #ECEDF0;
  animation: ${popIn} .26s cubic-bezier(.2,.7,.2,1);
  @media (max-width: 560px) {
    border-radius: 16px 16px 0 0;
    width: 100%;
    max-height: 92vh;
    align-self: flex-end;
  }
`;

const MHeader = styled.div`
  display: grid; grid-template-columns: 1fr auto;
  align-items: start; gap: 14px;
  padding: 18px 22px 16px;
  border-bottom: 1px solid #ECEDF0;
`;

const MTitle = styled.div`
  font-size: 19px; font-weight: 700; letter-spacing: -0.015em;
  color: #0B1220; line-height: 1.2;
`;

const MSub = styled.div`
  color: #8B93A6; font-size: 12.5px; margin-top: 3px;
`;

const CloseBtn = styled.button`
  width: 32px; height: 32px; border-radius: 8px;
  border: 1px solid transparent;
  background: transparent; cursor: pointer; color: #5C6577;
  display: inline-flex; align-items: center; justify-content: center;
  transition: background .15s, color .15s; flex-shrink: 0;
  &:hover { background: #F2F3F6; color: #0B1220; }
`;

const MBody = styled.div`
  padding: 18px 22px 4px;
  overflow-y: auto; min-height: 0;
  &::-webkit-scrollbar { width: 10px; }
  &::-webkit-scrollbar-thumb { background: #DFE1E6; border: 3px solid #fff; border-radius: 999px; }
  @media (max-width: 560px) { padding: 14px 16px 4px; }
`;

const Grp = styled.div`margin-bottom: 14px;`;

const GrpLabel = styled.label`
  display: flex; align-items: center; gap: 6px;
  font-size: 12.5px; font-weight: 600; color: #2A3142;
  margin-bottom: 6px;
`;

const Req = styled.span<{ $ok?: boolean }>`
  display: inline-flex; align-items: center; gap: 4px;
  font-size: 10.5px; font-weight: 700; letter-spacing: .04em;
  margin-left: auto;
  color: ${p => p.$ok ? '#168A50' : '#8B93A6'};
`;

const Asterisk = styled.span`color: #1E5BD8; margin-left: 1px;`;

const IField = styled.div<{ $valid?: boolean }>`
  position: relative; display: flex; align-items: center;
  border: 1px solid ${p => p.$valid ? '#B4E2C5' : '#DFE1E6'};
  border-radius: 10px;
  transition: border-color .15s, box-shadow .15s, background .15s;
  background: ${p => p.$valid ? 'linear-gradient(180deg,#FBFEFC,#fff)' : '#fff'};
  &:hover { border-color: ${p => p.$valid ? '#B4E2C5' : '#C8CDD6'}; }
  &:focus-within {
    border-color: ${p => p.$valid ? '#1FA060' : '#1E5BD8'};
    box-shadow: 0 0 0 3px ${p => p.$valid ? '#DCEFE3' : '#DDE7FC'};
  }
`;

const iInputBase = css`
  flex: 1; min-width: 0; border: 0; outline: none; background: transparent;
  font: 400 14px 'Manrope', system-ui, sans-serif; color: #0B1220;
  padding: 11px 12px 11px 14px;
  &::placeholder { color: #8B93A6; }
`;

const IInput = styled.input`${iInputBase}`;
const ITextArea = styled.textarea`
  ${iInputBase}
  resize: vertical; min-height: 72px; max-height: 200px;
  line-height: 1.5; padding: 11px 14px;
`;

const ISuffix = styled.span`
  position: absolute; right: 10px; top: 50%; transform: translateY(-50%);
  display: inline-flex; align-items: center; gap: 6px; pointer-events: none;
`;

const ValidCheck = styled.span`
  width: 20px; height: 20px; border-radius: 50%;
  background: #1FA060; color: #fff;
  display: inline-flex; align-items: center; justify-content: center;
  animation: ${tickPop} .22s cubic-bezier(.2,.8,.2,1);
`;

const ErrHint = styled.div`color: #B0271F; font-size: 11.5px; margin-top: 5px; padding-left: 2px;`;

const SpinnerCircle = styled.span`
  display: inline-block; width: 14px; height: 14px;
  border: 2px solid currentColor; border-top-color: transparent;
  border-radius: 50%; animation: ${spin} 0.7s linear infinite; flex-shrink: 0;
`;

// ─── SearchableCombo ──────────────────────────────────────────────────────────

const ComboWrapper = styled.div`position: relative;`;
const ComboTrigger = styled.button<{ $valid?: boolean }>`
  width: 100%; display: flex; align-items: center;
  background: ${p => p.$valid ? 'linear-gradient(180deg,#FBFEFC,#fff)' : '#fff'};
  border: 1px solid ${p => p.$valid ? '#B4E2C5' : '#DFE1E6'};
  border-radius: 10px; padding: 11px 36px 11px 14px;
  font: 400 14px 'Manrope', system-ui, sans-serif;
  color: ${p => p.$valid ? '#0B1220' : '#8B93A6'};
  text-align: left; cursor: pointer;
  transition: border-color .15s, box-shadow .15s; position: relative;
  &:hover { border-color: ${p => p.$valid ? '#B4E2C5' : '#C8CDD6'}; }
  &:focus {
    outline: none;
    border-color: ${p => p.$valid ? '#1FA060' : '#1E5BD8'};
    box-shadow: 0 0 0 3px ${p => p.$valid ? '#DCEFE3' : '#DDE7FC'};
  }
`;
const ComboTriggerSuffix = styled.span`
  position: absolute; right: 10px; top: 50%; transform: translateY(-50%);
  display: inline-flex; align-items: center; gap: 6px; pointer-events: none;
`;
const ComboDropdown = styled.div`
  position: absolute; top: calc(100% + 4px); left: 0; right: 0;
  background: #fff; border: 1px solid #DFE1E6; border-radius: 10px;
  box-shadow: 0 8px 24px rgba(0,0,0,.12); z-index: 10;
  overflow: hidden; display: flex; flex-direction: column; max-height: 240px;
`;
const ComboSearch = styled.input`
  width: 100%; padding: 9px 12px; border: 0; border-bottom: 1px solid #ECEDF0;
  font: 400 13px 'Manrope', system-ui, sans-serif; color: #0B1220; outline: none; box-sizing: border-box;
  &::placeholder { color: #8B93A6; }
  &:focus { border-bottom-color: #1E5BD8; }
`;
const ComboList = styled.div`
  overflow-y: auto; flex: 1;
  &::-webkit-scrollbar { width: 8px; }
  &::-webkit-scrollbar-thumb { background: #DFE1E6; border: 2px solid #fff; border-radius: 999px; }
`;
const ComboOption = styled.div<{ $selected?: boolean }>`
  padding: 9px 14px;
  font: ${p => p.$selected ? '600' : '400'} 13px 'Manrope', system-ui, sans-serif;
  color: ${p => p.$selected ? '#1E5BD8' : '#0B1220'};
  background: ${p => p.$selected ? '#EEF3FE' : 'transparent'};
  cursor: pointer; transition: background .1s;
  &:hover { background: ${p => p.$selected ? '#EEF3FE' : '#F2F3F6'}; }
`;

interface SearchableComboProps {
  value: string;
  placeholder: string;
  options: { value: string; label: string }[];
  onChange: (value: string) => void;
  valid?: boolean;
  searchable?: boolean;
}

function SearchableCombo({ value, placeholder, options, onChange, valid, searchable = true }: SearchableComboProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const wrapperRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const selectedLabel = options.find(o => o.value === value)?.label ?? '';
  const filtered = searchable
    ? options.filter(o => o.label.toLowerCase().includes(search.toLowerCase()))
    : options;

  useEffect(() => {
    if (!open) { setSearch(''); return; }
    if (searchable) setTimeout(() => searchRef.current?.focus(), 30);
  }, [open, searchable]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (!wrapperRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <ComboWrapper ref={wrapperRef}>
      <ComboTrigger type="button" $valid={valid} onClick={() => setOpen(o => !o)}
        aria-haspopup="listbox" aria-expanded={open}>
        {selectedLabel || placeholder}
        <ComboTriggerSuffix>
          {valid
            ? <ValidCheck><IconCheck size={12} sw={3.4} /></ValidCheck>
            : <span style={{ color: '#8B93A6' }}><IconChevronDown /></span>}
        </ComboTriggerSuffix>
      </ComboTrigger>
      {open && (
        <ComboDropdown role="listbox">
          {searchable && (
            <ComboSearch ref={searchRef} placeholder="Buscar…" value={search}
              onChange={e => setSearch(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Escape') setOpen(false);
                if (e.key === 'Enter' && filtered.length > 0) { onChange(filtered[0].value); setOpen(false); }
              }} />
          )}
          <ComboList>
            {value && (
              <ComboOption onClick={() => { onChange(''); setOpen(false); }}>
                <span style={{ color: '#8B93A6' }}>— Limpar seleção</span>
              </ComboOption>
            )}
            {filtered.length === 0 && (
              <ComboOption style={{ color: '#8B93A6', pointerEvents: 'none' }}>Nenhum resultado</ComboOption>
            )}
            {filtered.map(o => (
              <ComboOption key={o.value} $selected={o.value === value}
                onClick={() => { onChange(o.value); setOpen(false); }}
                role="option" aria-selected={o.value === value}>
                {o.label}
              </ComboOption>
            ))}
          </ComboList>
        </ComboDropdown>
      )}
    </ComboWrapper>
  );
}

// ─── Async contact search combo ───────────────────────────────────────────────

const AsyncComboInput = styled.div<{ $valid?: boolean }>`
  position: relative; display: flex; align-items: center;
  border: 1px solid ${p => p.$valid ? '#B4E2C5' : '#DFE1E6'};
  border-radius: 10px;
  background: ${p => p.$valid ? 'linear-gradient(180deg,#FBFEFC,#fff)' : '#fff'};
  transition: border-color .15s, box-shadow .15s;
  &:focus-within {
    border-color: ${p => p.$valid ? '#1FA060' : '#1E5BD8'};
    box-shadow: 0 0 0 3px ${p => p.$valid ? '#DCEFE3' : '#DDE7FC'};
  }
`;

interface ContactComboProps {
  value: number | null;
  onChange: (id: number | null, nome: string) => void;
  token?: string;
}

function ContactCombo({ value, onChange, token }: ContactComboProps) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<Eleitor[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [selectedName, setSelectedName] = useState('');
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});
  const wrapperRef = useRef<HTMLDivElement>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!value) { setSelectedName(''); setQuery(''); }
  }, [value]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (!wrapperRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Compute fixed-position dropdown coords so it escapes overflow:hidden/auto containers
  const computeDropdown = useCallback(() => {
    if (!wrapperRef.current) return;
    const rect = wrapperRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom - 12;
    setDropdownStyle({
      position: 'fixed',
      top: rect.bottom + 4,
      left: rect.left,
      width: rect.width,
      maxHeight: Math.min(240, spaceBelow),
      zIndex: 9999,
    });
  }, []);

  const search = useCallback((q: string) => {
    if (!q.trim()) { setResults([]); setOpen(false); return; }
    setLoading(true);
    computeDropdown();
    setOpen(true);
    apiFetch(`${API_BASE_URL}/api/v1/eleitores?search=${encodeURIComponent(q)}&limit=30`, {
      headers: token ? { 'Authorization': `Bearer ${token}` } : {},
    })
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(data => setResults(data.data || []))
      .catch(() => setResults([]))
      .finally(() => setLoading(false));
  }, [token, computeDropdown]);

  const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    setQuery(v);
    if (value) onChange(null, '');
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => search(v), 300);
  };

  const select = (el: Eleitor) => {
    onChange(el.id, el.nome);
    setSelectedName(el.nome);
    setQuery(el.nome);
    setOpen(false);
  };

  const clear = () => {
    onChange(null, '');
    setSelectedName('');
    setQuery('');
    setResults([]);
  };

  return (
    <ComboWrapper ref={wrapperRef}>
      <AsyncComboInput $valid={!!value}>
        <IInput
          type="text"
          placeholder="Digite o nome para buscar…"
          value={query || selectedName}
          onChange={handleInput}
          onFocus={() => { if (results.length > 0) { computeDropdown(); setOpen(true); } }}
          autoComplete="off"
        />
        {loading && <ISuffix><SpinnerCircle style={{ color: '#8B93A6' }} /></ISuffix>}
        {!loading && value && (
          <ISuffix style={{ pointerEvents: 'auto', cursor: 'pointer' }} onClick={clear}>
            <ValidCheck><IconCheck size={12} sw={3.4} /></ValidCheck>
          </ISuffix>
        )}
        {!loading && !value && query && (
          <ISuffix style={{ pointerEvents: 'auto', cursor: 'pointer' }} onClick={clear}>
            <span style={{ color: '#8B93A6' }}><IconX /></span>
          </ISuffix>
        )}
      </AsyncComboInput>
      {open && (
        <ComboDropdown style={dropdownStyle}>
          <ComboList>
            {results.length === 0 && !loading && (
              <ComboOption style={{ color: '#8B93A6', pointerEvents: 'none' }}>Nenhum contato encontrado</ComboOption>
            )}
            {results.map(el => (
              <ComboOption key={el.id} $selected={el.id === value}
                onClick={() => select(el)}>
                {el.nome}{el.telefone ? <span style={{ color: '#8B93A6', marginLeft: 6, fontSize: 12 }}>{el.telefone}</span> : null}
              </ComboOption>
            ))}
          </ComboList>
        </ComboDropdown>
      )}
    </ComboWrapper>
  );
}

// ─── Optional field tray ──────────────────────────────────────────────────────

const AddTray = styled.div<{ $done?: boolean }>`
  margin-top: 18px;
  padding: ${p => p.$done ? '11px 14px' : '13px 14px 12px'};
  background: ${p => p.$done ? '#E8F6EE' : '#FAFAFB'};
  border: 1px solid ${p => p.$done ? '#B4E2C5' : '#ECEDF0'};
  border-radius: 12px;
  transition: padding .2s, background .2s;
`;

const TrayHeader = styled.div`display: flex; align-items: center; gap: 8px; margin-bottom: 10px;`;

const TrayTitle = styled.span`
  font-size: 11px; font-weight: 700; letter-spacing: .08em; text-transform: uppercase;
  color: #5C6577; display: inline-flex; align-items: center; gap: 6px;
`;

const TrayHint = styled.span`margin-left: auto; font-size: 11px; font-weight: 600; color: #8B93A6;`;

const TrayChips = styled.div`display: flex; flex-wrap: wrap; gap: 6px;`;

const AddChip = styled.button`
  display: inline-flex; align-items: center; gap: 7px;
  padding: 6px 12px 6px 6px;
  border: 1px dashed #DFE1E6; background: #fff; border-radius: 999px;
  color: #2A3142; font: 600 12.5px 'Manrope', system-ui, sans-serif;
  cursor: pointer; transition: background .15s, border-color .15s, color .15s, box-shadow .15s;
  &:hover {
    background: #EEF3FE; border-style: solid; border-color: #BCD0FA;
    color: #1B50C2; box-shadow: 0 4px 10px -6px rgba(30,91,216,.25);
  }
`;

const ChipPlus = styled.span`
  width: 18px; height: 18px; border-radius: 50%;
  background: #F2F3F6; color: #5C6577;
  display: inline-flex; align-items: center; justify-content: center;
  transition: background .15s, color .15s;
  ${AddChip}:hover & { background: #DDE7FC; color: #1E5BD8; }
`;

const ChipIcon = styled.span`
  color: #8B93A6; display: inline-flex;
  ${AddChip}:hover & { color: #1B50C2; }
`;

const TrayDone = styled.div`
  display: inline-flex; align-items: center; gap: 8px;
  font-size: 12px; font-weight: 600; color: #1B6B3A;
`;

const ActField = styled.div`
  margin-bottom: 14px;
  animation: ${fieldSlideIn} .28s cubic-bezier(.2,.7,.2,1);
`;

const ActHeader = styled.div`display: flex; align-items: center; gap: 6px; margin-bottom: 6px;`;

const ActLabel = styled.label`
  display: inline-flex; align-items: center; gap: 7px;
  font-size: 12.5px; font-weight: 600; color: #2A3142;
`;

const ActRemove = styled.button`
  margin-left: auto; height: 22px; padding: 0 8px 0 6px; border-radius: 6px;
  border: 0; background: transparent; color: #8B93A6; cursor: pointer;
  display: inline-flex; align-items: center; gap: 3px;
  font: 600 11px 'Manrope', system-ui, sans-serif;
  transition: background .15s, color .15s;
  &:hover { background: #FDECEC; color: #B0271F; }
`;

const SubField = styled.div`
  margin-top: 10px;
  padding: 12px 14px;
  background: #F8F9FB;
  border: 1px solid #ECEDF0;
  border-radius: 10px;
  animation: ${fieldSlideIn} .22s cubic-bezier(.2,.7,.2,1);
`;

const SubLabel = styled.label`
  display: block; font-size: 12px; font-weight: 600; color: #5C6577;
  margin-bottom: 6px;
`;

// ─── Footer ───────────────────────────────────────────────────────────────────

const MFooter = styled.div`
  display: flex; align-items: center; justify-content: flex-end;
  gap: 12px; padding: 14px 22px;
  border-top: 1px solid #ECEDF0;
  background: linear-gradient(180deg, #fff, #FAFAFB);
`;

const FooterBtns = styled.div`display: flex; gap: 8px;`;

const Btn = styled.button`
  display: inline-flex; align-items: center; gap: 8px;
  height: 36px; padding: 0 16px; border-radius: 9px;
  border: 1px solid #DFE1E6; background: #fff; color: #0B1220;
  font: 600 13.5px 'Manrope', system-ui, sans-serif;
  cursor: pointer; transition: background .15s;
  &:hover { background: #F2F3F6; }
`;

const BtnPrimary = styled(Btn)`
  background: #1E5BD8; border-color: #1E5BD8; color: #fff;
  box-shadow: 0 1px 2px rgba(30,91,216,.35), 0 1px 0 rgba(255,255,255,.18) inset;
  &:hover:not(:disabled) { background: #1B50C2; }
  &:disabled {
    background: #DDE7FC; border-color: #DDE7FC; color: #fff;
    cursor: not-allowed; box-shadow: none;
  }
`;

// ─── Icons ────────────────────────────────────────────────────────────────────

function IconX() {
  return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 6l12 12M18 6L6 18"/></svg>;
}
function IconCheck({ size = 12, sw = 2.8 }: { size?: number; sw?: number }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round"><path d="M4 12l5 5L20 6"/></svg>;
}
function IconPlus({ size = 13 }: { size?: number }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M12 5v14M5 12h14"/></svg>;
}
function IconChevronDown() {
  return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9l6 6 6-6"/></svg>;
}
function IconText() {
  return <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M4 7V5h16v2M9 19h6M12 5v14"/></svg>;
}
function IconRuler() {
  return <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M3 21L21 3M3 21l5-5m-5 5l5 0m11-13l-5 5m5-5l0 5"/></svg>;
}
function IconScale() {
  return <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3v18M3 9l9-6 9 6M5 20h14"/><path d="M3 9l4 7H3M21 9l-4 7h4"/></svg>;
}
function IconUser() {
  return <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="7" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/></svg>;
}
function IconCalendar() {
  return <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="17" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/></svg>;
}
function IconSparkles() {
  return <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3l1.5 4.5L18 9l-4.5 1.5L12 15l-1.5-4.5L6 9l4.5-1.5z"/><path d="M19 16l.7 2L22 19l-2.3 1L19 22l-.7-2L16 19l2.3-1z"/></svg>;
}

// ─── Main component ───────────────────────────────────────────────────────────

const EMPTY_FORM: DemandFormData = {
  titulo: '', descricao: '', tipo: '', tamanho: '', natureza: '',
  cidade_id: null, solicitante_id: null, intermediador: '', previsao_conclusao: '',
};

const NewDemandModal: React.FC<Props> = ({ isOpen, onClose, onSubmit, initialCityId, zIndexOverride }) => {
  const { session } = useAuth();
  const { can } = usePermissionCheck();

  const [formData, setFormData] = useState<DemandFormData>(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [activeOpt, setActiveOpt] = useState<OptKey[]>([]);
  const [scopedCityIds, setScopedCityIds] = useState<number[] | null>(null);
  const titleRef = useRef<HTMLInputElement>(null);

  // Validation
  const v = {
    titulo: formData.titulo.trim().length >= 2,
    tipo:   !!formData.tipo,
    cidade: !!formData.cidade_id,
  };
  const allValid = v.titulo && v.tipo && v.cidade;

  // Scoped city fetch
  useEffect(() => {
    if (!isOpen || !session?.access_token) return;
    if (!can('demands.create') && can('demands.create.scoped_city')) {
      apiFetch(`${API_BASE_URL}/api/v1/demandas/minhas-cidades`, {
        headers: { 'Authorization': `Bearer ${session.access_token}` },
      })
        .then(r => r.ok ? r.json() : Promise.reject())
        .then(data => setScopedCityIds(data.data || []))
        .catch(() => setScopedCityIds([]));
    } else {
      setScopedCityIds(null);
    }
  }, [isOpen, session]);

  // Body scroll lock — skip if a parent modal already locked it
  useEffect(() => {
    if (isOpen && !initialCityId) {
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = ''; };
    }
  }, [isOpen, initialCityId]);

  // Focus on open, reset on close — pre-seed city if provided
  useEffect(() => {
    if (isOpen) {
      setSubmitted(false);
      setActiveOpt([]);
      setFormData({ ...EMPTY_FORM, cidade_id: initialCityId ?? null });
      setTimeout(() => titleRef.current?.focus(), 50);
    }
  }, [isOpen, initialCityId]);

  // Esc key
  useEffect(() => {
    if (!isOpen) return;
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [isOpen, onClose]);

  const cityOptions = useMemo(() => {
    const base = scopedCityIds !== null
      ? allCities.filter(c => scopedCityIds.includes(c.id))
      : allCities;
    return base.map(c => ({ value: String(c.id), label: c.nome }));
  }, [scopedCityIds]);

  const set = useCallback(<K extends keyof DemandFormData>(key: K, value: DemandFormData[K]) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  }, []);

  const addOpt = useCallback((key: OptKey) => {
    setActiveOpt(prev => prev.includes(key) ? prev : [...prev, key]);
  }, []);

  const removeOpt = useCallback((key: OptKey) => {
    setActiveOpt(prev => prev.filter(k => k !== key));
    switch (key) {
      case 'descricao':   set('descricao', ''); break;
      case 'tamanho':     set('tamanho', ''); break;
      case 'natureza':    set('natureza', ''); set('intermediador', ''); break;
      case 'solicitante': set('solicitante_id', null); break;
      case 'prazo':       set('previsao_conclusao', ''); break;
    }
  }, [set]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
    if (!allValid) return;
    setSubmitting(true);
    try {
      await onSubmit(formData);
    } catch (err) {
      console.error('Erro ao criar demanda:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const remaining = OPTIONAL_FIELDS.filter(f => !activeOpt.includes(f.key));

  if (!isOpen) return null;

  return (
    <Overlay $open={isOpen} $z={zIndexOverride} onClick={onClose}>
      <ModalShell onClick={e => e.stopPropagation()}>

        {/* ── Header ── */}
        <MHeader>
          <div>
            <MTitle>Nova demanda</MTitle>
            <MSub>Preencha o essencial — você pode completar o resto depois.</MSub>
          </div>
          <CloseBtn type="button" onClick={onClose} aria-label="Fechar">
            <IconX />
          </CloseBtn>
        </MHeader>

        {/* ── Body ── */}
        <MBody>
          <form id="new-demand-form" onSubmit={handleSubmit} noValidate>

            {/* Título */}
            <Grp>
              <GrpLabel htmlFor="nd-titulo">
                Título<Asterisk>*</Asterisk>
                <Req $ok={v.titulo}>
                  {v.titulo ? <><IconCheck size={11} sw={2.8} />OK</> : 'Obrigatório'}
                </Req>
              </GrpLabel>
              <IField $valid={v.titulo}>
                <IInput
                  ref={titleRef}
                  id="nd-titulo"
                  type="text"
                  placeholder="Ex: Melhoria da praça central"
                  value={formData.titulo}
                  onChange={e => set('titulo', e.target.value)}
                />
                {v.titulo && (
                  <ISuffix>
                    <ValidCheck><IconCheck size={12} sw={3.4} /></ValidCheck>
                  </ISuffix>
                )}
              </IField>
              {submitted && !v.titulo && <ErrHint>Informe o título (mínimo 2 caracteres).</ErrHint>}
            </Grp>

            {/* Tipo */}
            <Grp>
              <GrpLabel>
                Tipo<Asterisk>*</Asterisk>
                <Req $ok={v.tipo}>
                  {v.tipo ? <><IconCheck size={11} sw={2.8} />OK</> : 'Obrigatório'}
                </Req>
              </GrpLabel>
              <SearchableCombo
                value={formData.tipo}
                placeholder="Selecione o tipo"
                options={demandTypes.map(t => ({ value: t.value, label: t.label }))}
                onChange={val => set('tipo', val as DemandType | '')}
                valid={v.tipo}
                searchable={false}
              />
              {submitted && !v.tipo && <ErrHint>Selecione o tipo da demanda.</ErrHint>}
            </Grp>

            {/* Cidade */}
            <Grp>
              <GrpLabel>
                Cidade<Asterisk>*</Asterisk>
                <Req $ok={v.cidade}>
                  {v.cidade ? <><IconCheck size={11} sw={2.8} />OK</> : 'Obrigatório'}
                </Req>
              </GrpLabel>
              <SearchableCombo
                value={formData.cidade_id ? String(formData.cidade_id) : ''}
                placeholder="Selecione uma cidade"
                options={cityOptions}
                onChange={val => set('cidade_id', val ? Number(val) : null)}
                valid={v.cidade}
              />
              {submitted && !v.cidade && <ErrHint>Selecione a cidade.</ErrHint>}
            </Grp>

            {/* ── Active optional fields ── */}
            {activeOpt.map(key => (
              <OptField
                key={key}
                fieldKey={key}
                formData={formData}
                set={set}
                onRemove={() => removeOpt(key)}
                token={session?.access_token}
              />
            ))}

            {/* ── Add-more tray ── */}
            {remaining.length === 0 ? (
              <AddTray $done>
                <TrayDone>
                  <IconCheck size={12} sw={2.8} />
                  Pronto — todos os detalhes opcionais foram adicionados.
                </TrayDone>
              </AddTray>
            ) : (
              <AddTray>
                <TrayHeader>
                  <TrayTitle><IconSparkles />Adicionar mais detalhes</TrayTitle>
                  <TrayHint>opcional · escolha o que precisar</TrayHint>
                </TrayHeader>
                <TrayChips>
                  {remaining.map(f => (
                    <AddChip key={f.key} type="button" onClick={() => addOpt(f.key)}>
                      <ChipPlus><IconPlus size={11} /></ChipPlus>
                      <ChipIcon>{f.icon}</ChipIcon>
                      {f.label}
                    </AddChip>
                  ))}
                </TrayChips>
              </AddTray>
            )}

          </form>
        </MBody>

        {/* ── Footer ── */}
        <MFooter>
          <FooterBtns>
            <Btn type="button" onClick={onClose} disabled={submitting}>Cancelar</Btn>
            <BtnPrimary
              type="submit"
              form="new-demand-form"
              disabled={!allValid || submitting}
            >
              {submitting ? <SpinnerCircle /> : <IconPlus size={13} />}
              {submitting ? 'Criando…' : 'Criar demanda'}
            </BtnPrimary>
          </FooterBtns>
        </MFooter>

      </ModalShell>
    </Overlay>
  );
};

// ─── Optional field renderer ──────────────────────────────────────────────────

interface OptFieldProps {
  fieldKey: OptKey;
  formData: DemandFormData;
  set: <K extends keyof DemandFormData>(key: K, value: DemandFormData[K]) => void;
  onRemove: () => void;
  token?: string;
}

function OptField({ fieldKey, formData, set, onRemove, token }: OptFieldProps) {
  const def = OPTIONAL_FIELDS.find(d => d.key === fieldKey)!;

  let inner: React.ReactNode = null;

  switch (fieldKey) {
    case 'descricao':
      inner = (
        <IField>
          <ITextArea
            placeholder="Descreva a demanda em detalhes…"
            value={formData.descricao}
            onChange={e => set('descricao', e.target.value)}
            rows={3}
            autoFocus
          />
        </IField>
      );
      break;

    case 'tamanho':
      inner = (
        <SearchableCombo
          value={formData.tamanho}
          placeholder="Selecione o tamanho"
          options={demandSizes.map(s => ({ value: s.value, label: s.label }))}
          onChange={val => set('tamanho', val as DemandSize | '')}
          valid={!!formData.tamanho}
          searchable={false}
        />
      );
      break;

    case 'natureza':
      inner = (
        <>
          <SearchableCombo
            value={formData.natureza}
            placeholder="Selecione a natureza"
            options={demandNatures.map(n => ({ value: n.value, label: n.label }))}
            onChange={val => {
              set('natureza', val as DemandNature | '');
              if (val !== 'INTERMEDIADA') set('intermediador', '');
            }}
            valid={!!formData.natureza}
            searchable={false}
          />
          {formData.natureza === 'INTERMEDIADA' && (
            <SubField>
              <SubLabel htmlFor="nd-intermediador">Intermediador</SubLabel>
              <IField>
                <IInput
                  id="nd-intermediador"
                  type="text"
                  placeholder="Nome do intermediador"
                  value={formData.intermediador}
                  onChange={e => set('intermediador', e.target.value)}
                  autoFocus
                />
              </IField>
            </SubField>
          )}
        </>
      );
      break;

    case 'solicitante':
      inner = (
        <ContactCombo
          value={formData.solicitante_id}
          onChange={(id) => set('solicitante_id', id)}
          token={token}
        />
      );
      break;

    case 'prazo':
      inner = (
        <IField>
          <IInput
            type="date"
            value={formData.previsao_conclusao}
            onChange={e => set('previsao_conclusao', e.target.value)}
            autoFocus
          />
        </IField>
      );
      break;

    default:
      return null;
  }

  return (
    <ActField>
      <ActHeader>
        <ActLabel>
          <span style={{ color: '#8B93A6', display: 'inline-flex' }}>{def.icon}</span>
          {def.label}
        </ActLabel>
        <ActRemove type="button" onClick={onRemove} aria-label={`Remover ${def.label}`}>
          <IconX />Remover
        </ActRemove>
      </ActHeader>
      {inner}
    </ActField>
  );
}

export default NewDemandModal;
